import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ConsentSign() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [acked, setAcked] = useState({});
  const [subjectName, setSubjectName] = useState("");
  const [subjectEmail, setSubjectEmail] = useState("");
  const [typedSig, setTypedSig] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/clinical/consent/forms/public/${token}`)
      .then(({ data }) => setForm(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Form not available."));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const acknowledged = Object.entries(acked).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/clinical/consent/sign", {
        token, subject_name: subjectName, subject_email: subjectEmail || null,
        acknowledged, typed_signature: typedSig,
      });
      setDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed.");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="vq-card p-8 max-w-lg text-center" data-testid="consent-error-state">
        <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
        <div className="font-display font-bold text-lg text-slate-900">Consent form unavailable</div>
        <div className="text-sm text-slate-600 mt-2">{err}</div>
      </div>
    </div>
  );

  if (!form) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

  if (done) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="vq-card p-8 max-w-lg text-center" data-testid="consent-success-state">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <div className="font-display font-bold text-xl text-slate-900">Consent recorded</div>
        <div className="text-sm text-slate-600 mt-3">
          Consent ID: <span className="font-mono">{done.consent_id}</span>
          <br />Signed at {new Date(done.signed_at).toLocaleString()}
        </div>
        <div className="text-xs text-slate-500 mt-4">You may close this window.</div>
      </div>
    </div>
  );

  const allAcked = (form.required_acknowledgements || []).every((a) => acked[a]);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded bg-slate-900 text-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="font-display font-bold text-slate-900">VitaQMS eConsent</div>
        </div>
        <div className="vq-card p-6">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Study {form.study_id} · Version {form.version}</div>
          <div className="font-display font-extrabold text-2xl text-slate-900 mt-1" data-testid="consent-title">{form.title}</div>

          <div className="mt-4 prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap border border-slate-200 rounded p-3 max-h-96 overflow-y-auto"
               data-testid="consent-body">
            {form.body_markdown}
          </div>

          {(form.required_acknowledgements || []).length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">I acknowledge the following</div>
              <div className="space-y-2">
                {form.required_acknowledgements.map((a, i) => (
                  <label key={a} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`ack-${i}`} checked={!!acked[a]}
                           onChange={(e) => setAcked({ ...acked, [a]: e.target.checked })} />
                    <span>{a}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-3">
            <div>
              <Label>Your full legal name</Label>
              <Input data-testid="sub-name" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} />
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input data-testid="sub-email" type="email" value={subjectEmail} onChange={(e) => setSubjectEmail(e.target.value)} />
            </div>
            <div>
              <Label>Type your full name to sign</Label>
              <Input data-testid="sub-typed-sig" value={typedSig} onChange={(e) => setTypedSig(e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">By typing your name and clicking submit, you provide an electronic signature.</div>
            </div>
          </div>

          <Button className="w-full mt-6" data-testid="sub-submit" onClick={submit}
                  disabled={busy || !allAcked || subjectName.length < 2 || typedSig.length < 2}>
            {busy ? "Signing…" : "I consent"}
          </Button>
        </div>
      </div>
    </div>
  );
}
